import {
  Briefcase,
  CalendarDays,
  CalendarOff,
  IdCard,
  Sparkles,
  User,
} from "lucide-react";
import { usePerson } from "@/hooks/usePeople";
import { Badge } from "@/components/ui/badge";
import { matchContextPath } from "../match";
import type { ContextParams, NavContextDef, NavItem } from "../types";

export const personPath = (id: string) => `/people/${id}`;

const PERSON_TYPE_LABELS: Record<string, string> = {
  employee: "Employee",
  freelancer: "Freelancer",
  contact: "Contact",
};

function usePersonItems({ id }: ContextParams): NavItem[] {
  const { data: person } = usePerson(id);
  const base = personPath(id);
  const items: NavItem[] = [
    { key: "details", label: "Details", href: base, exact: true, icon: IdCard },
    {
      key: "bookings",
      label: "Bookings",
      href: `${base}/bookings`,
      icon: CalendarDays,
    },
    { key: "skills", label: "Skills", href: `${base}/skills`, icon: Sparkles },
  ];
  // Employment data only exists for employees (L5: hidden as today).
  if (person?.person_type === "employee") {
    items.push(
      {
        key: "employment",
        label: "Employment",
        href: `${base}/employment`,
        icon: Briefcase,
      },
      {
        key: "absences-overtime",
        label: "Absences & overtime",
        href: `${base}/absences-overtime`,
        icon: CalendarOff,
      },
    );
  }
  return items;
}

function usePersonHeader({ id }: ContextParams) {
  const { data: person } = usePerson(id);
  if (!person) return {};
  const name = `${person.first_name} ${person.last_name}`.trim();
  return {
    kicker: person.number ?? null,
    title: name,
    status: person.person_type ? (
      <Badge variant="secondary">
        {PERSON_TYPE_LABELS[person.person_type] ?? person.person_type}
      </Badge>
    ) : null,
  };
}

export const personContext: NavContextDef = {
  type: "person",
  typeLabel: "Person",
  icon: User,
  match: (pathname) =>
    matchContextPath("/people/:id", pathname, {
      id: ["new", "employees", "freelancers"],
    }),
  key: ({ id }) => `person:${id}`,
  base: ({ id }) => personPath(id),
  parent: () => ({ label: "People", href: "/people" }),
  useHeader: usePersonHeader,
  useItems: usePersonItems,
};
